import { useActivityLog } from "@features/bx/BulkBillGeneration/hooks";
import { Clock } from "lucide-react";

interface ActivityLogTabProps {
  batchId: string;
}

interface ActivityItem {
  id: string;
  title: string;
  description: string;
  performedBy: string;
  timestamp: string;
  icon?: React.ReactNode;
}

export const ActivityLogTab = ({ batchId }: ActivityLogTabProps) => {
  const { data: activities, isLoading } = useActivityLog(batchId);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!activities || activities?.length === 0) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground text-center py-8">
          No activity recorded yet
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Activity Log</h3>
      </div>

      <div className="space-y-4">
        {activities?.map((activity: ActivityItem, index: number) => (
          <div
            key={activity.id || index}
            className="flex items-start gap-4 pb-4 border-b border-gray-100 last:border-0"
          >
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                {activity.icon || <Clock className="h-5 w-5 text-blue-600" />}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {activity.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Performed by:{" "}
                    <span className="font-medium">{activity.performedBy}</span>
                  </p>
                  <p className="text-sm text-gray-700 mt-2">
                    {activity.description}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <p className="text-sm text-gray-500 whitespace-nowrap">
                    {activity.timestamp}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
